import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/base/Pagination";
import { categories } from "@/mocks/videos";
import { searchVideos } from "@/utils/video";
import type { Video } from "@/types/video";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";

  const [keyword, setKeyword] = useState(q);
  const [type, setType] = useState("全部");
  const [page, setPage] = useState(1);

  useDocumentMeta(
    q ? `搜索结果 - 西瓜视频` : "搜索 - 西瓜视频",
    q
      ? `西瓜视频「${q}」搜索结果，为你匹配短视频、影视剧、综艺节目等多种类型的相关视频内容。`
      : "在西瓜视频搜索你想看的短视频、影视剧、综艺节目等视频内容。",
    "西瓜视频,视频搜索,在线视频,短视频,影视剧,综艺节目"
  );

  useEffect(() => {
    setKeyword(q);
    setPage(1);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [keyword, type]);

  const results = useMemo<Video[]>(() => {
    return searchVideos(keyword, type === "全部" ? undefined : type);
  }, [keyword, type]);

  const totalPages = Math.ceil(results.length / PAGE_SIZE);
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (keyword.trim()) next.set("q", keyword.trim());
    setSearchParams(next);
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-1">
        搜索视频
      </h1>
      {q ? (
        <p className="text-sm text-foreground-500 mb-5">
          搜索关键词：<span className="text-primary-600 font-medium">{q}</span>
        </p>
      ) : (
        <p className="text-sm text-foreground-500 mb-5">输入关键词，发现更多精彩内容</p>
      )}

      <form onSubmit={handleSubmit} className="mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频标题、类型或标签…"
              className="w-full pl-10 pr-3 py-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition"
            />
          </div>
          <button
            type="submit"
            className="px-7 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>
      </form>

      <div className="flex items-center gap-2 mb-5 overflow-x-auto xg-scroll-hide">
        <span className="text-sm text-foreground-400 shrink-0">类型</span>
        {["全部", ...categories.map((c) => c.name)].map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => setType(name)}
            className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
              type === name
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:text-primary-600"
            }`}
          >
            {name}
          </button>
        ))}
      </div>

      <p className="text-sm text-foreground-500 mb-4">
        共找到 <span className="text-primary-600 font-medium">{results.length}</span> 个相关视频
      </p>

      {pageItems.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {pageItems.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
          <div className="mt-8">
            <Pagination current={page} total={totalPages} onChange={setPage} />
          </div>
        </>
      ) : (
        <div className="py-20 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
          </span>
          <h2 className="text-base font-bold text-foreground-900 mb-2">未找到相关视频</h2>
          <p className="text-sm text-foreground-500 mb-6">
            试试换个关键词，或调整筛选类型重新搜索。
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setType("全部");
                setSearchParams(new URLSearchParams());
              }}
              className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重新搜索
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}